import { useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { logError } from '../utils/logger';
import { addToast } from '@heroui/react';
import { Booking, IBookingQuery } from '../utils/type';
import { stateConvertToShortCode } from '../utils/common';

const SERVICE_ENDPOINT = import.meta.env.VITE_APP_BACKEND_SERVICE;

export interface BookingsResponse {
  bookings: Booking[];
  total: number;
  page: number;
  pages: number;
  limit: number;
  totalPages: number;
}

export interface UpdateBookingStatusData {
  status:
    | 'Pending'
    | 'Confirmed'
    | 'Finished'
    | 'Cancelled'
    | 'Completed'
    | 'InProgress'
    | 'Rescheduled';
  updatedAt?: string;
}

export interface RescheduleBookingData {
  bookingId: string;
  newDate: string; // YYYY-MM-DD
  newFrom: string; // Start time, e.g., "10:00 AM"
  newTo: string; // End time, e.g., "10:30 AM"
  staffId?: string;
  note?: string;
  performedBy?: string;
  // Backward compatibility fields (optional)
  newTime?: string; // Time range string like "10:00 AM-11:00 AM"
  appointmentDate?: string; // New appointment date
  appointmentTimeFrom?: string; // New start time
  appointmentTimeTo?: string; // New end time
  reason?: string; // Optional reason for rescheduling
  rescheduleNote?: string; // Optional note from customer
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  rating?: number;
  userId?: string;
  status?: string;
  appointmentDate?: string;
}

export interface PromoCode {
  promoCode: string;
  discountId: string;
}

/**
 * Get all bookings for a user
 */
export const getUserBookings = async (
  params: PaginationParams = {}
): Promise<BookingsResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      userId,
      status = '',
      appointmentDate = '',
    } = params;
    console.log(
      `Fetching bookings - page: ${page}, limit: ${limit}, userId: ${userId}, status: ${status}, appointmentDate: ${appointmentDate}`
    );

    if (!userId) {
      throw new Error(
        'User ID is required to fetch bookings. Please ensure you are logged in.'
      );
    }

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (userId) {
      queryParams.append('userId', userId);
    }

    if (status && status !== 'all') {
      queryParams.append('search', status);
    }

    if (appointmentDate) {
      queryParams.append('appointmentDate', appointmentDate);
    }

    const response = await apiClient.get(
      `${SERVICE_ENDPOINT}${Path.getBookings}?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching bookings:', error);

    // Handle specific error cases
    if (error.response?.status === 403) {
      const errorMsg =
        'Access denied. You may not have permission to view bookings or your session may have expired.';

      logError(errorMsg, error);
      addToast({
        title: 'Access Denied',
        description: 'Your session may have expired. Please log in again.',
        radius: 'md',
        color: 'danger',
      });
      throw new Error(errorMsg);
    } else if (error.response?.status === 401) {
      const errorMsg =
        'Authentication required. Please log in to view your bookings.';
      logError(errorMsg, error);
      addToast({
        title: 'Authentication Required',
        description: 'Please log in to view your bookings.',
        radius: 'md',
        color: 'warning',
      });
      throw new Error(errorMsg);
    }

    logError('Error fetching bookings:', error);
    throw error;
  }
};

export const getBookingAnalytics = async (userId: string) => {
  try {
    const res = await apiClient.get(
      `${SERVICE_ENDPOINT}${Path.bookingAnalytics}?customerId=${userId}`
    );
    return res.data?.analytics;
  } catch (error: unknown) {
    logError(`Error fetching booking analytics`, { error });
    throw error;
  }
};

export const getProviderPublicStats = async (providerId: string) => {
  try {
    const res = await apiClient.get(
      `${SERVICE_ENDPOINT}${Path.providerPublicStats}`.replace(
        ':providerId',
        providerId
      )
    );
    return res.data?.analytics;
  } catch (error: unknown) {
    logError('Error fetching provider public stats', { error });
    throw error;
  }
};

/**
 * Get a specific booking by ID
 */
export const getBookingById = async (bookingId: string): Promise<Booking> => {
  try {
    console.log(`Fetching booking with ID: ${bookingId}`);
    const url = `${SERVICE_ENDPOINT}${Path.getBookingById}`.replace(
      ':bookingId',
      bookingId
    );
    const response = await apiClient.get(url);
    console.log('Booking fetched successfully:', response.data);
    return response.data.booking;
  } catch (error: any) {
    console.error(`Error fetching booking ${bookingId}:`, error);
    logError(`Error fetching booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Update booking status
 */
export const updateBookingStatus = async (
  bookingId: string,
  data: UpdateBookingStatusData
): Promise<Booking> => {
  try {
    console.log(`Updating booking ${bookingId} status to:`, data.status);

    const updatePayload = {
      ...data,
      updatedAt: data.updatedAt || new Date().toISOString(),
    };

    const url = `${SERVICE_ENDPOINT}${Path.getBookingById}`.replace(
      ':bookingId',
      bookingId
    );
    const response = await apiClient.put(url, updatePayload);
    console.log('Booking status updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error(`Error updating booking ${bookingId} status:`, error);
    logError(`Error updating booking ${bookingId} status:`, error);
    throw error;
  }
};

/**
 * Confirm a booking (change status from Pending to Confirmed)
 */
export const confirmBooking = async (bookingId: string): Promise<Booking> => {
  try {
    return await updateBookingStatus(bookingId, { status: 'Confirmed' });
  } catch (error: any) {
    logError(`Error confirming booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Mark booking as finished (typically after review submission)
 */
export const finishBooking = async (bookingId: string): Promise<Booking> => {
  try {
    return await updateBookingStatus(bookingId, { status: 'Finished' });
  } catch (error: any) {
    logError(`Error finishing booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId: string): Promise<Booking> => {
  try {
    return await updateBookingStatus(bookingId, { status: 'Cancelled' });
  } catch (error: any) {
    logError(`Error cancelling booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Reschedule a booking with new date and time
 */
export const rescheduleBooking = async (
  rescheduleData: RescheduleBookingData
): Promise<Booking> => {
  try {
    console.log(`Rescheduling booking ${rescheduleData.bookingId} to:`, {
      newDate: rescheduleData.newDate,
      newFrom: rescheduleData.newFrom,
      newTo: rescheduleData.newTo,
      staffId: rescheduleData.staffId,
      note: rescheduleData.note,
      performedBy: rescheduleData.performedBy,
    });

    // If only newTime provided, derive newFrom/newTo
    let newFrom = rescheduleData.newFrom;
    let newTo = rescheduleData.newTo;
    if (
      (!newFrom || !newTo) &&
      rescheduleData.newTime &&
      rescheduleData.newTime.includes('-')
    ) {
      const [startTime, endTime] = rescheduleData.newTime.split('-');
      newFrom = newFrom || startTime.trim();
      newTo = newTo || endTime.trim();
    }

    // Prepare payload per backend requirements
    const reschedulePayload = {
      newDate: rescheduleData.newDate,
      newFrom,
      newTo,
      staffId: rescheduleData.staffId,
      note:
        rescheduleData.note ||
        rescheduleData.rescheduleNote ||
        rescheduleData.reason,
      performedBy: rescheduleData.performedBy,
    };

    console.log('Sending reschedule payload:', reschedulePayload);
    console.log(
      'API URL will be:',
      `service/api/v1/booking/${rescheduleData.bookingId}/reschedule`
    );

    const baseUrl = `${Path.getBookingById}`.replace(
      ':bookingId',
      rescheduleData.bookingId
    );
    const response = await apiClient.put(
      `${SERVICE_ENDPOINT}${baseUrl}/reschedule`,
      reschedulePayload
    );
    console.log('Booking rescheduled successfully:', response.data);
    return response.data;
  } catch (error: any) {
    logError(`Error rescheduling booking ${rescheduleData.bookingId}:`, error);
    throw error;
  }
};

/**
 * Get available time slots for a specific date and provider
 */
export const getAvailableTimeSlots = async (
  providerId: string,
  date: string
): Promise<{
  date: string;
  timeSlots: Array<{
    time: string;
    available: boolean;
    reason?: string;
    popularity?: 'low' | 'medium' | 'high';
  }>;
}> => {
  try {
    const response = await apiClient.get(
      `service/api/v1/booking/availability/${providerId}`,
      {
        params: { date },
      }
    );

    return response.data;
  } catch (error: any) {
    logError(`Error fetching available time slots:`, error);
    return generateFallbackTimeSlots(date);
  }
};

/**
 * Generate fallback time slots when API is unavailable
 */
const generateFallbackTimeSlots = (date: string) => {
  const timeSlots = [];
  const currentDate = new Date();
  const selectedDate = new Date(date);

  // Generate time slots from 9 AM to 5 PM with 30-minute intervals
  for (let hour = 9; hour <= 17; hour++) {
    for (const minute of [0, 30]) {
      // Skip if we're at 5:30 PM
      if (hour === 17 && minute === 30) continue;

      const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
      const period = hour < 12 ? 'AM' : 'PM';
      const startTime = `${formattedHour}:${minute === 0 ? '00' : minute} ${period}`;

      // Calculate end time (30 minutes later)
      const endHour = minute === 30 ? hour + 1 : hour;
      const endMinute = minute === 30 ? 0 : 30;
      const formattedEndHour = endHour % 12 === 0 ? 12 : endHour % 12;
      const endPeriod = endHour < 12 ? 'AM' : 'PM';
      const endTime = `${formattedEndHour}:${endMinute === 0 ? '00' : endMinute} ${endPeriod}`;

      const timeString = `${startTime}-${endTime}`;

      // Determine availability
      let available = true;
      let reason = '';
      let popularity: 'low' | 'medium' | 'high' = 'medium';

      // If the selected date is today, only show future time slots
      if (
        selectedDate.toDateString() === currentDate.toDateString() &&
        (hour < currentDate.getHours() ||
          (hour === currentDate.getHours() &&
            minute <= currentDate.getMinutes()))
      ) {
        available = false;
        reason = 'Past time';
      }

      // Simulate some random unavailability
      if (available && Math.random() > 0.7) {
        available = false;
        reason = 'Fully booked';
      }

      // Set popularity
      if (hour >= 10 && hour <= 14) {
        popularity = 'high';
      } else if (hour === 9 || hour >= 16) {
        popularity = 'low';
      }

      timeSlots.push({
        time: timeString,
        available,
        reason: available ? '' : reason,
        popularity: available ? popularity : undefined,
      });
    }
  }

  return {
    date,
    timeSlots,
  };
};

export const getStaffById = async (id: string) => {
  try {
    const res = await apiClient.get(
      `${SERVICE_ENDPOINT}/${Path.getStaff}` + `/${id}`
    );
    return res.data;
  } catch (error: unknown) {
    logError(`Error fetching staff with ID ${id}`);
    throw error;
  }
};

export const getTimeSlotRelatedStaff = async (data: any) => {
  try {
    const res = await apiClient.post(
      `${SERVICE_ENDPOINT}/${Path.checkTimeslotAvailable}`,
      data
    );
    return res.data;
  } catch (error: unknown) {
    logError('Error getting staff booking time slot : ', { error });
    throw error;
  }
};

export const bookingTotalCalculation = async (data: any) => {
  try {
    const res = await apiClient.post(
      `${SERVICE_ENDPOINT}/${Path.totalCalculation}`,
      data
    );
    return res.data;
  } catch (error: unknown) {
    logError('Error booking total calculation  : ', { error });
    throw error;
  }
};

export const verifyPromoCode = async (data: PromoCode) => {
  try {
    const res = await apiClient.post(
      `${SERVICE_ENDPOINT}/${Path.verifyPromoCode}`,
      data
    );
    return res.data;
  } catch (error: unknown) {
    logError('Error verify promo code  : ', { error });
    throw error;
  }
};

// ── Hold / Release (booking-slot atomicity) ───────────────────────────────
// Before committing a booking, the customer portal tentatively reserves the
// slot (+ specific staff) via POST /booking/hold. If the booking create fails,
// the client compensates with POST /booking/release (best-effort). See
// ai-work/docs/tasks/booking-atomicity/plan.md §4.8.

export interface HoldSlotPayload {
  serviceId: string;
  appointmentDate: string;
  timeSlotId: string;
  from: string;
  to: string;
  staffId: string; // concrete staffId, or the ANY_STAFF sentinel
}

export interface HoldSlotResponse {
  success: boolean;
  holdToken: string;
  expiresAt: string;
  serviceId: string;
  date: string;
  from: string;
  to: string;
  staffId: string;
  slot?: any;
}

export interface ReleaseSlotPayload {
  serviceId: string;
  date: string;
  from: string;
  to: string;
  holdToken: string;
}

/**
 * Tentatively reserve a slot (and staff) before committing a booking.
 * The 201 response carries `holdToken` + the echoed slot identity that the
 * subsequent POST /booking must attach so the backend can confirm atomically.
 *
 * A 409 with `reason: 'capacity' | 'staff_conflict'` means the slot/staff was
 * taken between grid render and submit — the caller should refresh the grid and
 * NOT proceed to POST /booking. 501/404 mean the backend does not support the
 * hold endpoint (older release) and the caller should fall back to a legacy
 * direct booking.
 */
export const holdBookingSlot = async (
  data: HoldSlotPayload
): Promise<HoldSlotResponse> => {
  try {
    const res = await apiClient.post(`${SERVICE_ENDPOINT}/booking/hold`, data);
    return res.data;
  } catch (error: unknown) {
    logError('Error holding booking slot: ', { error });
    throw error;
  }
};

/**
 * Best-effort compensation: release a held slot that was never confirmed.
 * The caller wraps this so a release failure never masks the original error.
 */
export const releaseHeldSlot = async (
  data: ReleaseSlotPayload
): Promise<void> => {
  try {
    await apiClient.post(`${SERVICE_ENDPOINT}/booking/release`, data);
  } catch (error: unknown) {
    logError('Error releasing held booking slot (best-effort): ', { error });
    throw error;
  }
};

// CHINTHAKA NEW CODE

//CHECK TIMESLOT AVAILABILITY BY DATE AND SERVICE ID
export const useCheckTimesSlotAvailability = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await apiClient.post(Path.checkTimeslotAvailable, data);
      return res.data;
    },
    onError: (error: any) => {
      console.error('Failed to check timeslot availability:', error);
      addToast({
        title: 'Failed to check timeslot availability',
        description: 'Please try again later.',
        radius: 'md',
        color: 'danger',
      });
    },
  });
};

// BOOKING reschedule
export const useBookingReschedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data }: { data: any }) => {
      const res = await apiClient.put(
        `${SERVICE_ENDPOINT}/${Path.bookings}/${data?.bookingId}/reschedule`,
        data
      );
      return res.data;
    },
    onSuccess: () => {
      addToast({
        title: 'Booking reschedule successful',
        description: 'Booking rescheduled successfully.',
        radius: 'md',
        color: 'success',
      });

      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['USER_BOOKINGS'],
      });
    },
    onError: (error: any) => {
      console.error('Failed to booking reschedule:', error);
      addToast({
        title: 'Booking reschedule failed',
        description: 'An error occurred while rescheduling the booking.',
        radius: 'md',
        color: 'danger',
      });
    },
  });
};

/**
 * Get all bookings with pagination
 * GET service/api/v1/booking
 */
export const getBookings = async (
  params: PaginationParams = {}
): Promise<BookingsResponse> => {
  try {
    const { page = 1, limit = 10, userId, status } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (userId) {
      queryParams.append('userId', userId);
    }

    if (status) {
      queryParams.append('status', status);
    }

    const response = await apiClient.get(
      `${Path.getBookings}?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: any) {
    logError('Error fetching all bookings:', { error });
    throw error;
  }
};

/**
 * Get booking by reference code
 * GET service/api/v1/booking/referenceCode/:referenceCode
 */
export const getBookingByReference = async (
  referenceCode: string
): Promise<Booking> => {
  try {
    const url = `${SERVICE_ENDPOINT}${Path.getBookingByReference}`.replace(
      ':referenceCode',
      referenceCode
    );
    const response = await apiClient.get(url);
    return response.data.booking;
  } catch (error: any) {
    logError(`Error fetching booking with reference ${referenceCode}:`, {
      error,
    });
    throw error;
  }
};

/**
 * Accept a provider-created online-payment booking (customer-facing pay page).
 * POST service/api/v1/booking/:bookingId/accept — resolves by bookingId OR
 * referenceCode. Ownership is masked server-side; errors: 404 (not found /
 * not the caller's booking), 409 (state conflict, e.g. not awaiting
 * acceptance), 410 (payment link expired). Idempotent — re-accepting an
 * already-accepted booking resolves with { alreadyAccepted: true }.
 */
export const acceptBooking = async (
  bookingIdOrRef: string
): Promise<{ booking: Booking; alreadyAccepted?: boolean }> => {
  try {
    const url = `${SERVICE_ENDPOINT}${Path.bookingAccept}`.replace(
      ':bookingId',
      bookingIdOrRef
    );
    const response = await apiClient.post(url);
    return response.data;
  } catch (error: any) {
    logError(`Error accepting booking ${bookingIdOrRef}:`, error);
    throw error;
  }
};

/**
 * Decline a provider-created online-payment booking (customer-facing pay
 * page). POST service/api/v1/booking/:bookingId/decline — same resolver and
 * error contract as acceptBooking; cancels the booking (slot release +
 * standard cancellation notifications).
 */
export const declineBooking = async (
  bookingIdOrRef: string
): Promise<{ booking: Booking }> => {
  try {
    const url = `${SERVICE_ENDPOINT}${Path.bookingDecline}`.replace(
      ':bookingId',
      bookingIdOrRef
    );
    const response = await apiClient.post(url);
    return response.data;
  } catch (error: any) {
    logError(`Error declining booking ${bookingIdOrRef}:`, error);
    throw error;
  }
};

/**
 * Get all confirmed bookings
 * GET service/api/v1/booking/confirmed
 */
export const getConfirmedBookings = async (
  params: PaginationParams = {}
): Promise<BookingsResponse> => {
  try {
    const { page = 1, limit = 10, userId } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (userId) {
      queryParams.append('userId', userId);
    }

    const response = await apiClient.get(
      `${SERVICE_ENDPOINT}${Path.getConfirmedBookings}?${queryParams.toString()}`
    );

    return response.data;
  } catch (error: any) {
    logError('Error fetching confirmed bookings:', { error });
    throw error;
  }
};

export const completeBookingStatus = async (id: string, newStatus: string) => {
  try {
    const res = await apiClient.put(
      `${SERVICE_ENDPOINT}/${Path.bookingStatus.replace(':id', id)}`,
      {
        newStatus: newStatus,
      }
    );
    return res.data;
  } catch (error: unknown) {
    logError('Error update booking state: ', { error });
    throw error;
  }
};

// Types for tax calculation payload and response
export interface SellerRegistration {
  registered: boolean;
  // mapping of tax scheme/code to registration number, flexible keys
  numbers?: Record<string, string>;
}

export interface CustomerExemption {
  exempt: boolean;
  reason?: string;
}

// place-of-supply address (QA-30): the payment service requires this when
// PLACE_OF_SUPPLY_BASIS (server config) is RECIPIENT_ADDRESS or
// SUPPLIER_ADDRESS -- RECIPIENT_ADDRESS is the service's own default, so
// recipientAddress is effectively required today, not optional.
export interface TaxAddress {
  country: string; // ISO-3166 alpha-2, e.g. 'CA'
  region: string; // province/state short code, e.g. 'ON' -- must be non-empty
  postalCode?: string;
}

export interface TaxCalculationRequest {
  amount: number;
  amountType?: 'exclusive' | 'inclusive';
  currency: string; // e.g., 'cad'
  country: string; // ISO country code, e.g., 'CA'
  region?: string; // state/province short code
  postalCode?: string;
  taxCategory?: string; // e.g., 'home-cleaning'
  supplyDate?: string; // YYYY-MM-DD
  sellerRegistration?: SellerRegistration;
  customerExemption?: CustomerExemption;
  correlationId?: string;
  recipientAddress?: TaxAddress;
  supplierAddress?: TaxAddress;
  serviceLocation?: TaxAddress;
  // allow additional backend-specific fields
  [key: string]: any;
}

export interface TaxLine {
  name?: string;
  rate?: number;
  amount: number;
  jurisdiction?: string;
  code?: string;
}

export interface TaxCalculationResponse {
  success?: boolean;
  totalTax: number;
  totalAmount: number;
  currency?: string;
  lines?: TaxLine[];
  // Quote object returned by payment service (e.g. used by BookingPage)
  quote?: {
    totalTaxMinor: number; // tax in minor units (cents)
    totalAmountMinor: number;
    currency: string;
    [key: string]: any;
  };
  // raw backend response for forwards compatibility
  raw?: any;
}

export interface BuildTaxCalculationPayloadInput {
  serviceId: string;
  businessId: string;
  subtotal: number; // major units (dollars) -- converted to minor units (cents) here
  address: { state?: string; postalCode?: string };
  service: {
    categoryId?: string;
    categoryName?: string;
    categorySlug?: string | null;
    subCategorySlug?: string | null;
  };
  supplyDate?: string; // YYYY-MM-DD; defaults to today
}

/**
 * Builds the request body for POST /payment/api/v1/tax/calculate.
 *
 * Extracted to a pure function (rather than inlined in BookingPage.tsx) so
 * it can be tested directly against the payment service's actual contract
 * (gigmosaic-payment-service TaxMiddleware.js / TaxQuery.js) without
 * rendering the whole booking page. This is the fix for QA-30: the payment
 * service's PLACE_OF_SUPPLY_BASIS defaults to RECIPIENT_ADDRESS, which
 * makes `recipientAddress` effectively required, not optional -- the flat
 * top-level country/region/postalCode fields alone are not enough.
 * recipientAddress carries the same customer-address data, just nested.
 */
export const buildTaxCalculationPayload = (
  input: BuildTaxCalculationPayloadInput
): TaxCalculationRequest => {
  const region = stateConvertToShortCode(input.address.state) || '';
  const postalCode = input.address.postalCode || '';

  return {
    amount: Math.round(Number(input.subtotal || 0) * 100),
    amountType: 'exclusive',
    currency: 'cad',
    country: 'CA',
    region,
    postalCode,
    businessId: input.businessId,
    taxCategory:
      input.service.subCategorySlug || input.service.categorySlug || '',
    supplyDate: input.supplyDate || moment().format('YYYY-MM-DD'),
    sellerRegistration: {
      registered: false,
      numbers: {},
    },
    customerExemption: {
      exempt: false,
    },
    correlationId: `booking-${input.serviceId}-${Date.now()}`,
    recipientAddress: {
      country: 'CA',
      region,
      postalCode,
    },
  };
};

export const calculateTax = async (
  data: TaxCalculationRequest
): Promise<TaxCalculationResponse> => {
  try {
    const res = await apiClient.post(Path.taxCalculation, data);
    return res.data;
  } catch (error: unknown) {
    logError('Error calculating tax from payment service: ', { error });
    throw error;
  }
};

export const updateBookingStatusToCancel = async (
  id: string,
  newStatus: string,
  note?: string
) => {
  try {
    const res = await apiClient.put(
      `${SERVICE_ENDPOINT}/${Path.bookingStatus.replace(':id', id)}`,
      {
        newStatus: newStatus,
        note,
      }
    );
    return res.data;
  } catch (error: unknown) {
    logError('Error update booking state to cancel: ', { error });
    throw error;
  }
};

export const getAllBookingsByQuery = () => async (data: IBookingQuery) => {
  try {
    const formatQuery = {
      ...data,
      filters: data.filters ? JSON.stringify(data.filters) : undefined,
    };

    const res = await apiClient.get(
      `${SERVICE_ENDPOINT}${Path.bookingGetByQuery}`,
      {
        params: formatQuery,
      }
    );

    return res.data.data;
  } catch (error: unknown) {
    console.error('Error get all bookings by query', error);
    throw error;
  }
};
